import { ErrorMessage, Field, Form, Formik } from "formik";
import { LockSimple } from "@phosphor-icons/react";
import { FC } from "react";
import * as yup from "yup";
import { motion } from "framer-motion";
import useSetSecurityQuestion from "../hooks/useSetSecurityQuestion";
import { toast } from "sonner";
import { securityQuestions } from "../helpers/array";

const schema = yup.object().shape({
  question: yup.string().required("Please enter a security question."),
  answer: yup
    .string()
    .required("Please provide an answer to the security question."),
});

const SetSecurityQuestionModal: FC<{
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ setShowModal }) => {
  const {
    savingSecurityQuestions,
    setSecurityQuestion,
    setSecurityQuestionError,
    isSetSecurityQuestionError,
  } = useSetSecurityQuestion({
    onSuccess() {
      setShowModal(false);
      toast.success("Security Question has been set.");
    },
  });
  return (
    <motion.div
      initial={{
        opacity: 0,
      }}
      animate={{
        opacity: 1,
      }}
      exit={{
        opacity: 0,
      }}
      className="fixed top-0 left-0 bg-deepBlue/80 backdrop-blur-sm w-full h-screen grid place-items-center p-5 z-50"
    >
      <Formik
        validationSchema={schema}
        initialValues={{
          question: "",
          answer: "",
        }}
        onSubmit={(values) => {
          setSecurityQuestion({
            security_answer: values.answer,
            security_question: values.question,
          });
        }}
      >
        <Form className="bg-white max-w-sm w-full p-5 rounded-xl shadow-sm flex items-center flex-col gap-5">
          <div className="p-2 bg-gray-100 rounded-full ring-8 ring-gray-50">
            <LockSimple size={28} />
          </div>
          <div className="flex items-center flex-col">
            <div className="font-medium">Set Security Question</div>
            <p className="text-gray-500 text-center">
              Set a security question only you can answer.
            </p>
          </div>
          <div className="flex flex-col gap-2 max-w-md w-full">
            <label htmlFor="pin" className="text-sm font-bold text-gray-700">
              Security Question*
            </label>
            <div className="rounded-lg border border-gray-300 shadow flex items-center w-full relative overflow-hidden focus-within:outline-deepBlue focus-within:outline accent-deepBlue">
              <Field
                as="select"
                id="question"
                name="question"
                className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full outline-none"
              >
                <option value="" label="Select a question" />
                {securityQuestions.map((question) => (
                  <option key={question.value} value={question.value}>
                    {question.label}
                  </option>
                ))}
              </Field>
            </div>
            <ErrorMessage
              name="question"
              component={motion.div}
              className="text-red-500"
            />
          </div>
          <div className="flex flex-col gap-2 max-w-md w-full">
            <label htmlFor="pin" className="text-sm font-bold text-gray-700">
              Answer*
            </label>
            <div className="rounded-lg border border-gray-300 shadow flex items-center w-full relative overflow-hidden focus-within:outline-deepBlue focus-within:outline accent-deepBlue">
              <Field
                component="textarea"
                autoComplete="off"
                id="answer"
                name="answer"
                placeholder="Enter your answer"
                className="px-4 py-3 bg-transparent flex-1 rounded-lg focus:outline-none"
              />
            </div>
            <ErrorMessage
              name="answer"
              component={motion.div}
              className="text-red-500"
            />
          </div>
          {isSetSecurityQuestionError && (
            <div className="text-red-500 text-left flex w-full">
              {setSecurityQuestionError?.message}
            </div>
          )}
          <div className="flex items-center gap-5 w-full">
            <button
              type="button"
              aria-label="Cancel"
              onClick={() => setShowModal(false)}
              className="p-3 px-5 flex-1 border-red-100 border-2 rounded-lg font-bold bg-red-50/50 text-red-500 text-sm"
            >
              Cancel
            </button>
            <button
              disabled={savingSecurityQuestions}
              className="p-3 px-5 flex-1 border-visaro-gray-700 border-2 rounded-lg font-bold bg-deepBlue text-white text-sm"
            >
              {savingSecurityQuestions ? "Saving..." : "Save"}
            </button>
          </div>
        </Form>
      </Formik>
    </motion.div>
  );
};

export default SetSecurityQuestionModal;
